// src/features/dashboard/hooks/useTodayActivity.js
import { useState, useEffect, useMemo } from 'react'
import { getDb } from '../../../lib/db'
import { getTodayStartEAT } from '../../../features/dashboard/utils/dashboard.time'

/**
 * @typedef {Object} TodaySaleItem
 * @property {string=} product_name
 * @property {{ name?: string }=} products
 * @property {number=} quantity
 * @property {number=} unit_price
 * @property {number=} total_amount
 */

/**
 * @typedef {Object} TodayActivityItem
 * @property {'sale' | 'expense'} type
 * @property {string} id
 * @property {string} date
 * @property {number} amount
 * @property {string} display_name
 * @property {string[]=} payment_methods
 * @property {string|null=} payment_account
 * @property {TodaySaleItem[]=} sale_items
 * @property {string|null=} category
 * @property {string|null=} description
 */

/**
 * @typedef {Object} TodaySummary
 * @property {number} totalSales
 * @property {number} totalExpenses
 * @property {number} net
 * @property {number} saleCount
 * @property {number} expenseCount
 */

const EMPTY_SUMMARY = Object.freeze({
  totalSales: 0,
  totalExpenses: 0,
  net: 0,
  saleCount: 0,
  expenseCount: 0,
})

function createEmptySummary() {
  return { ...EMPTY_SUMMARY }
}

function asNumber(value) {
  return Number(value || 0)
}

function uniqueValues(values) {
  return [...new Set(values.filter(Boolean))]
}

function groupBySaleId(docs) {
  return docs.reduce((acc, doc) => {
    const row = doc?.toJSON ? doc.toJSON() : doc
    if (!row?.sale_id) return acc
    if (!acc[row.sale_id]) acc[row.sale_id] = []
    acc[row.sale_id].push(row)
    return acc
  }, {})
}

function normalizeSale(row) {
  const saleItems = Array.isArray(row.sale_items) ? row.sale_items : []
  const payments = Array.isArray(row.sale_payments) ? row.sale_payments : []
  const paymentMethods = uniqueValues(
    payments.map((payment) => payment?.payment_method || payment?.method)
  )
  const amount = asNumber(
    row.total_amount ?? saleItems.reduce((sum, item) => sum + asNumber(item?.total_amount), 0)
  )
  const firstItem = saleItems[0]
  const displayName =
    row.display_name ||
    (saleItems.length > 1
      ? `${firstItem?.product_name || firstItem?.products?.name || 'Sale'} +${saleItems.length - 1} more`
      : firstItem?.product_name || firstItem?.products?.name || 'Sale')

  return {
    ...row,
    type: 'sale',
    amount,
    display_name: displayName,
    payment_account: paymentMethods[0] || row.payment_account || null,
    payment_methods: paymentMethods,
    sale_items: saleItems,
    date: row.date || row.created_at || null,
    lifecycle_state: row.lifecycle_state || 'finalized',
  }
}

function normalizeExpense(row) {
  return {
    ...row,
    type: 'expense',
    amount: asNumber(row.amount),
    display_name: row.description || row.category || 'Expense',
    category: row.category || null,
    description: row.description || null,
    payment_account: row.payment_account || null,
    date: row.date || row.created_at || null,
    lifecycle_state: row.lifecycle_state || 'finalized',
  }
}

function computeSummary(transactions) {
  const totalSales = transactions
    .filter((transaction) => transaction.type === 'sale')
    .reduce((sum, transaction) => sum + asNumber(transaction.amount), 0)

  const totalExpenses = transactions
    .filter((transaction) => transaction.type === 'expense')
    .reduce((sum, transaction) => sum + asNumber(transaction.amount), 0)

  return {
    totalSales,
    totalExpenses,
    net: totalSales - totalExpenses,
    saleCount: transactions.filter((transaction) => transaction.type === 'sale').length,
    expenseCount: transactions.filter((transaction) => transaction.type === 'expense').length,
  }
}

export function useTodayActivity(dashboard) {
  const { business, branchId } = dashboard
  const [todayTransactions, setTodayTransactions] = useState([])
  const [todaySummary, setTodaySummary] = useState(createEmptySummary)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const businessId = business?.id

  const dateRange = useMemo(() => {
    const todayKey = getTodayStartEAT()
    const start = new Date(`${todayKey}T00:00:00+03:00`)
    const end = new Date(start)
    end.setDate(end.getDate() + 1)

    return {
      todayStart: start.toISOString(),
      todayEnd: end.toISOString(),
    }
  }, [])

  const selector = useMemo(() => {
    const branchSelector = branchId ? { branch_id: branchId } : {}

    return {
      business_id: businessId,
      _deleted: { $ne: true },
      ...branchSelector,
    }
  }, [businessId, branchId])

  useEffect(() => {
    if (!businessId) {
      setError(null)
      setLoading(false)
      return undefined
    }

    let active = true
    let subscriptions = []

    const init = async () => {
      try {
        setLoading(true)
        setError(null)

        const db = await getDb()
        const collections = []

        if (db.sales) {
          collections.push({
            collection: db.sales,
            type: 'sale',
            selector: {
              ...selector,
              date: {
                $gte: dateRange.todayStart,
                $lt: dateRange.todayEnd,
              },
              lifecycle_state: 'finalized',
            },
          })
        }

        if (db.expenses) {
          collections.push({
            collection: db.expenses,
            type: 'expense',
            selector: {
              ...selector,
              date: {
                $gte: dateRange.todayStart,
                $lt: dateRange.todayEnd,
              },
            },
          })
        }

        const refresh = async () => {
          const rowsByType = await Promise.all(
            collections.map(async (config) => {
              const docs = await config.collection
                .find({
                  selector: config.selector,
                  sort: [{ date: 'desc' }, { id: 'desc' }],
                })
                .exec()

              return docs.map((doc) => {
                const row = doc.toJSON ? doc.toJSON() : doc

                if (config.type === 'sale') {
                  return {
                    ...row,
                    type: 'sale',
                    date: row.date || row.created_at || null,
                  }
                }

                return normalizeExpense(row)
              })
            })
          )

          const saleRows = rowsByType.flat().filter((row) => row.type === 'sale')
          const saleIds = saleRows.map((row) => row.id).filter(Boolean)
          const [saleItemDocs, salePaymentDocs] = await Promise.all([
            saleIds.length > 0
              ? (db.sale_items
                ? db.sale_items.find({ selector: { sale_id: { $in: saleIds }, _deleted: { $ne: true } } }).exec().catch(() => [])
                : Promise.resolve([]))
              : Promise.resolve([]),
            saleIds.length > 0
              ? (db.sale_payments
                ? db.sale_payments.find({ selector: { sale_id: { $in: saleIds }, _deleted: { $ne: true } } }).exec().catch(() => [])
                : Promise.resolve([]))
              : Promise.resolve([]),
          ])

          const saleItemsBySaleId = groupBySaleId(saleItemDocs || [])
          const salePaymentsBySaleId = groupBySaleId(salePaymentDocs || [])

          const hydratedRowsByType = rowsByType.map((rows) =>
            rows.map((row) => {
              if (row.type !== 'sale') return row
              return normalizeSale({
                ...row,
                sale_items: saleItemsBySaleId[row.id] || row.sale_items || [],
                sale_payments: salePaymentsBySaleId[row.id] || row.sale_payments || [],
              })
            })
          )

          if (!active) return

          const ordered = hydratedRowsByType
            .flat()
            .sort((left, right) => {
              const leftTime = new Date(left.date || 0).getTime() || 0
              const rightTime = new Date(right.date || 0).getTime() || 0

              if (leftTime !== rightTime) {
                return rightTime - leftTime
              }

              return String(right.id || '').localeCompare(String(left.id || ''))
            })

          setTodayTransactions(ordered)
          setTodaySummary(computeSummary(ordered))
          setLoading(false)
        }

        await refresh()

        subscriptions = collections.map((config) =>
          config.collection.find({ selector: config.selector }).$.subscribe({
            next: () => {
              refresh().catch((err) => {
                if (!active) return
                console.error('Today activity refresh error:', err)
                setError(err)
                setLoading(false)
              })
            },
            error: (err) => {
              if (!active) return
              console.error('Today activity subscription error:', err)
              setError(err)
              setLoading(false)
            },
          })
        )
      } catch (err) {
        if (!active) return
        console.error('Failed to initialize today activity:', err)
        setError(err)
        setLoading(false)
      }
    }

    init()

    return () => {
      active = false
      subscriptions.forEach((subscription) => subscription?.unsubscribe?.())
    }
  }, [businessId, branchId, selector, dateRange.todayEnd, dateRange.todayStart])

  return { todayTransactions, todaySummary, loading, error }
}
